import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, firstValueFrom, Observable, of, throwError } from 'rxjs';
import { ConfigService } from 'src/app/common/services/config/config.service';
import {
  IAppState,
  IFrontendSettings,
} from '../ngrx.stores/frontend.settings/states';
import { frontendSettingsSelector } from '../ngrx.stores/frontend.settings/selectors';
import { ApiErrorHandler } from 'src/app/common/services/api/api.error.handler';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapService {
  private readonly apiLoadedSubject = new BehaviorSubject<boolean>(false);
  private readonly apiLoadingSubject = new BehaviorSubject<boolean>(false);
  private apiLoadPromise: Promise<boolean> | null = null;
  private scriptElement: HTMLScriptElement | null = null;
  
  apiLoaded$: Observable<boolean> = this.apiLoadedSubject.asObservable();
  apiLoading$: Observable<boolean> = this.apiLoadingSubject.asObservable();
  
  isLoaded = false;
  placesLibrary: google.maps.PlacesLibrary | null = null;
  
  ngRxStore = inject(Store<IAppState>);
  apiErrorHandler = inject(ApiErrorHandler);
  frontendSettings: IFrontendSettings | null = null;

  constructor(public httpClient: HttpClient) {
    this.initializeSettings();
  }

  private initializeSettings(): void {
    this.ngRxStore
      .pipe(select(frontendSettingsSelector))
      .subscribe((settings) => {
        if (settings) {
          this.frontendSettings = settings;
          console.log('🔧 [GoogleMapService] Frontend settings loaded');
        }
      });
  }

  loadGoogleMapApi(): Observable<boolean> {
    console.log('🚀 [GoogleMapService] Loading Google Maps API requested');
    
    // Return cached result if already loaded and validated
    if (this.isLoaded && this.validateApiAvailability()) {
      console.log('✅ [GoogleMapService] API already loaded and validated');
      return of(true);
    }

    // Return existing promise if already loading
    if (this.apiLoadPromise) {
      console.log('⏳ [GoogleMapService] API loading already in progress');
      return new Observable(subscriber => {
        this.apiLoadPromise!.then(
          result => {
            subscriber.next(result);
            subscriber.complete();
          },
          error => subscriber.error(error)
        );
      });
    }

    // Start fresh loading process
    this.apiLoadPromise = this.performApiLoad();
    
    return new Observable(subscriber => {
      this.apiLoadPromise!.then(
        result => {
          subscriber.next(result);
          subscriber.complete();
        },
        error => subscriber.error(error)
      );
    });
  }

  private async performApiLoad(): Promise<boolean> {
    console.log('🔄 [GoogleMapService] Starting API load process');
    
    try {
      this.apiLoadingSubject.next(true);
      
      // Step 1: Clean up any existing scripts
      this.cleanupExistingScripts();
      
      // Step 2: Validate prerequisites
      if (!this.validatePrerequisites()) {
        throw new Error('Prerequisites validation failed');
      }

      const googleKey = this.frontendSettings!.customSettings.dataSource.googleKey;
      console.log('🔑 [GoogleMapService] Using API key:', googleKey.substring(0, 15) + '...');

      // Step 3: Load the script and wait for it
      const success = await this.loadScriptAndWait(googleKey);
      
      if (success) {
        console.log('✅ [GoogleMapService] API loaded successfully');
        this.onApiLoadSuccess();
        return true;
      } else {
        throw new Error('Failed to load Google Maps API');
      }

    } catch (error) {
      console.error('❌ [GoogleMapService] API load failed:', error);
      this.handleApiLoadError(error);
      return false;
    } finally {
      this.apiLoadingSubject.next(false);
    }
  }

  private cleanupExistingScripts(): void {
    console.log('🧹 [GoogleMapService] Cleaning up existing scripts');
    
    // Remove any existing Google Maps scripts
    const existingScripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    existingScripts.forEach(script => {
      console.log('🗑️ [GoogleMapService] Removing existing script:', script.getAttribute('src'));
      script.remove();
    });

    // Reset script reference
    this.scriptElement = null;
    
    // Clear global callback if exists
    if ((window as any).initGoogleMaps) {
      delete (window as any).initGoogleMaps;
    }
  }

  private loadScriptAndWait(apiKey: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      console.log('📡 [GoogleMapService] Loading script with callback approach');
      
      // Create unique callback name
      const callbackName = 'initGoogleMaps_' + Date.now();
      
      // Set up global callback
      (window as any)[callbackName] = () => {
        console.log('📥 [GoogleMapService] Google Maps callback fired');
        
        // Clean up callback
        delete (window as any)[callbackName];
        
        // Wait a bit for everything to initialize
        setTimeout(() => {
          if (this.validateApiAvailability()) {
            resolve(true);
          } else {
            reject(new Error('API loaded but validation failed'));
          }
        }, 500);
      };

      // Create script element
      this.scriptElement = document.createElement('script');
      this.scriptElement.src = `https://maps.googleapis.com/maps/api/js?libraries=places&key=${apiKey}&callback=${callbackName}`;
      this.scriptElement.async = true;
      this.scriptElement.defer = true;
      this.scriptElement.id = 'google-maps-api-script';

      // Set up timeout
      const timeout = setTimeout(() => {
        console.log('⏰ [GoogleMapService] Script loading timeout');
        this.cleanupFailedLoad(callbackName);
        reject(new Error('Script loading timeout'));
      }, 15000);

      // Handle script load success
      this.scriptElement.onload = () => {
        console.log('📥 [GoogleMapService] Script element loaded');
        // Don't resolve here - wait for callback
        clearTimeout(timeout);
      };

      // Handle script load error
      this.scriptElement.onerror = (error) => {
        console.log('❌ [GoogleMapService] Script loading failed:', error);
        clearTimeout(timeout);
        this.cleanupFailedLoad(callbackName);
        reject(new Error('Script loading failed'));
      };

      try {
        document.head.appendChild(this.scriptElement);
        console.log('✅ [GoogleMapService] Script added to document head');
      } catch (error) {
        clearTimeout(timeout);
        this.cleanupFailedLoad(callbackName);
        reject(new Error('Failed to add script to document'));
      }
    });
  }

  private cleanupFailedLoad(callbackName: string): void {
    // Remove callback
    if ((window as any)[callbackName]) {
      delete (window as any)[callbackName];
    }
    
    // Remove script if exists
    if (this.scriptElement) {
      this.scriptElement.remove();
      this.scriptElement = null;
    }
  }

  private validateApiAvailability(): boolean {
    try {
      const hasGoogle = !!(window as any).google;
      const hasMaps = !!(window as any).google?.maps;
      const hasPlaces = !!(window as any).google?.maps?.places;
      const hasLatLng = !!(window as any).google?.maps?.LatLng;
      const hasMap = !!(window as any).google?.maps?.Map;
      
      console.log('🔍 [GoogleMapService] API Validation:', {
        hasGoogle,
        hasMaps,
        hasPlaces,
        hasLatLng,
        hasMap
      });
      
      return hasGoogle && hasMaps && hasPlaces && hasLatLng && hasMap;
    } catch (error) {
      console.error('❌ [GoogleMapService] API validation error:', error);
      return false;
    }
  }

  private validatePrerequisites(): boolean {
    if (!this.frontendSettings?.customSettings?.dataSource?.googleKey) {
      const errorMessage = 'Google API key not found in frontend settings';
      console.error(`❌ [GoogleMapService] ${errorMessage}`);
      this.apiErrorHandler.showError(errorMessage);
      return false;
    }

    const apiKey = this.frontendSettings.customSettings.dataSource.googleKey;
    if (!apiKey || apiKey.trim().length < 10) {
      const errorMessage = 'Invalid Google API key';
      console.error(`❌ [GoogleMapService] ${errorMessage}`);
      this.apiErrorHandler.showError(errorMessage);
      return false;
    }

    return true;
  }

  private onApiLoadSuccess(): void {
    try {
      console.log('✅ [GoogleMapService] Validating loaded API');
      
      // Final validation
      if (!this.validateApiAvailability()) {
        throw new Error('API validation failed after loading');
      }

      // Set up places library reference
      this.placesLibrary = (window as any).google.maps.places;
      this.isLoaded = true;
      this.apiLoadedSubject.next(true);
      
      console.log('🎉 [GoogleMapService] API loaded and validated successfully');
      console.log('📍 [GoogleMapService] Places library available:', !!this.placesLibrary);

    } catch (error) {
      console.error('❌ [GoogleMapService] API validation failed:', error);
      this.handleApiLoadError(error);
    }
  }

  private handleApiLoadError(error: any): void {
    const errorMessage = this.getErrorMessage(error);
    console.error('❌ [GoogleMapService] API load error:', errorMessage, error);
    
    this.isLoaded = false;
    this.placesLibrary = null;
    this.apiLoadedSubject.next(false);
    this.apiLoadPromise = null; // Reset so it can be retried
    
    this.apiErrorHandler.showError(`Failed to load Google Maps: ${errorMessage}`);
  }

  private getErrorMessage(error: any): string {
    if (error?.name === 'TimeoutError') {
      return 'Request timed out. Please check your internet connection.';
    }
    
    if (error?.status === 0) {
      return 'Network error. Please check your internet connection.';
    }
    
    if (error?.status >= 400 && error?.status < 500) {
      return 'Invalid API key or configuration.';
    }
    
    if (error?.message) {
      return error.message;
    }
    
    return 'Unknown error occurred while loading Google Maps API';
  }

  // Enhanced API readiness check
  isApiReady(): boolean {
    const ready = this.isLoaded && this.validateApiAvailability() && !!this.placesLibrary;
    console.log('🔍 [GoogleMapService] API ready check:', ready);
    return ready;
  }

  // Improved wait method with better debugging
  async waitForApiReady(timeoutMs: number = 15000): Promise<boolean> {
    console.log('⏳ [GoogleMapService] Waiting for API to be ready...');
    
    if (this.isApiReady()) {
      console.log('✅ [GoogleMapService] API already ready');
      return true;
    }

    // If not loading and not loaded, start loading
    if (!this.apiLoadPromise && !this.isLoaded) {
      console.log('🚀 [GoogleMapService] Starting API load from waitForApiReady');
      this.loadGoogleMapApi().subscribe({
        next: (loaded) => console.log('📡 [GoogleMapService] Load subscription result:', loaded),
        error: (error) => console.error('❌ [GoogleMapService] Load subscription error:', error)
      });
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      let attemptCount = 0;
      
      const checkInterval = setInterval(() => {
        attemptCount++;
        const elapsed = Date.now() - startTime;
        
        console.log(`🔄 [GoogleMapService] API ready check attempt ${attemptCount} (${elapsed}ms elapsed)`);
        
        if (this.isApiReady()) {
          clearInterval(checkInterval);
          console.log(`✅ [GoogleMapService] API ready after ${elapsed}ms (${attemptCount} attempts)`);
          resolve(true);
          return;
        }

        // More detailed logging during wait
        if (attemptCount % 10 === 0) { // Every second
          console.log('🔍 [GoogleMapService] Still waiting...', {
            elapsed: elapsed + 'ms',
            isLoaded: this.isLoaded,
            hasGoogle: !!(window as any).google,
            hasMaps: !!(window as any).google?.maps,
            hasPlaces: !!(window as any).google?.maps?.places,
            hasPlacesLibrary: !!this.placesLibrary
          });
        }

        if (elapsed >= timeoutMs) {
          clearInterval(checkInterval);
          console.error(`❌ [GoogleMapService] API ready timeout after ${elapsed}ms (${attemptCount} attempts)`);
          resolve(false);
          return;
        }
      }, 100);
    });
  }

  // Method to get the places library safely
  getPlacesLibrary(): google.maps.PlacesLibrary | null {
    if (!this.isApiReady()) {
      console.warn('⚠️ [GoogleMapService] Places library not available - API not ready');
      return null;
    }
    return this.placesLibrary;
  }

  // Enhanced distance calculation with validation
  getDistanceFromLatLng(lat1: number, lon1: number, lat2: number, lon2: number): number {
    if (!this.isValidCoordinate(lat1, lon1) || !this.isValidCoordinate(lat2, lon2)) {
      console.warn('⚠️ [GoogleMapService] Invalid coordinates provided for distance calculation');
      return 0;
    }

    try {
      const R = 6371; // Radius of the earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const d = R * c; // Distance in km
      return d * 1000; // Convert to meters
    } catch (error) {
      console.error('❌ [GoogleMapService] Error calculating distance:', error);
      return 0;
    }
  }

  private isValidCoordinate(lat: number, lng: number): boolean {
    return (
      typeof lat === 'number' && 
      typeof lng === 'number' && 
      !isNaN(lat) && 
      !isNaN(lng) &&
      lat >= -90 && 
      lat <= 90 && 
      lng >= -180 && 
      lng <= 180
    );
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  // Enhanced direction service with better error handling
  async getDirection(
    attractions: any[],
    travelMode: TravelMode,
    callback: (response: google.maps.DirectionsRoute) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    console.log('🧭 [GoogleMapService] Getting directions for', attractions.length, 'attractions');
    
    try {
      // Validate inputs
      if (!this.validateDirectionsInput(attractions, callback)) {
        return;
      }

      // Ensure API is ready
      const apiReady = await this.waitForApiReady();
      if (!apiReady) {
        const errorMsg = 'Google Maps API failed to load for directions';
        this.handleDirectionsError(errorMsg, errorCallback);
        return;
      }

      const { origin, destination, waypoints } = this.processAttractions(attractions);
      
      if (!origin || !destination) {
        const errorMsg = 'Invalid origin or destination coordinates';
        this.handleDirectionsError(errorMsg, errorCallback);
        return;
      }

      await this.requestDirections(origin, destination, waypoints, travelMode, callback, errorCallback);

    } catch (error) {
      const errorMsg = `Unexpected error in getDirection: ${error instanceof Error ? error.message : 'Unknown error'}`;
      this.handleDirectionsError(errorMsg, errorCallback);
    }
  }

  private validateDirectionsInput(attractions: any[], callback: Function): boolean {
    if (!attractions || !Array.isArray(attractions) || attractions.length < 2) {
      const errorMsg = 'Invalid attractions data - need at least 2 locations';
      console.error(`❌ [GoogleMapService] ${errorMsg}`);
      return false;
    }

    if (typeof callback !== 'function') {
      console.error('❌ [GoogleMapService] Invalid callback function provided');
      return false;
    }

    return true;
  }

  private processAttractions(attractions: any[]): {
    origin: google.maps.LatLng | null;
    destination: google.maps.LatLng | null;
    waypoints: google.maps.DirectionsWaypoint[];
  } {
    let origin: google.maps.LatLng | null = null;
    let destination: google.maps.LatLng | null = null;
    const waypoints: google.maps.DirectionsWaypoint[] = [];

    attractions.forEach((attraction, index) => {
      const lat = attraction.item?.address?.lat || attraction.item?.lat;
      const lng = attraction.item?.address?.lng || attraction.item?.lng;
      
      if (!this.isValidCoordinate(lat, lng)) {
        console.warn(`⚠️ [GoogleMapService] Invalid coordinates for attraction at index ${index}`);
        return;
      }

      const latlng = new google.maps.LatLng(lat, lng);
      
      if (index === 0) {
        origin = latlng;
      } else if (index === attractions.length - 1) {
        destination = latlng;
      } else {
        waypoints.push({ location: latlng, stopover: true });
      }
    });

    return { origin, destination, waypoints };
  }

  private async requestDirections(
    origin: google.maps.LatLng,
    destination: google.maps.LatLng,
    waypoints: google.maps.DirectionsWaypoint[],
    travelMode: TravelMode,
    callback: (response: google.maps.DirectionsRoute) => void,
    errorCallback?: (error: string) => void
  ): Promise<void> {
    const directionsService = new google.maps.DirectionsService();

    const request: google.maps.DirectionsRequest = {
      origin,
      destination,
      waypoints,
      optimizeWaypoints: false,
      travelMode: google.maps.TravelMode[travelMode],
    };

    directionsService.route(request, (response, status) => {
      if (status === google.maps.DirectionsStatus.OK && response?.routes?.[0]) {
        console.log('✅ [GoogleMapService] Directions retrieved successfully');
        callback(response.routes[0]);
      } else {
        const errorMsg = `Directions service failed with status: ${status}`;
        console.error('❌ [GoogleMapService] Direction Service error', response, status);
        this.handleDirectionsError(errorMsg, errorCallback);
      }
    });
  }

  private handleDirectionsError(errorMsg: string, errorCallback?: (error: string) => void): void {
    console.error(`❌ [GoogleMapService] ${errorMsg}`);
    if (errorCallback) {
      errorCallback(errorMsg);
    }
  }

  // Method to reset the service state (useful for testing or error recovery)
  reset(): void {
    console.log('🔄 [GoogleMapService] Resetting service state');
    
    this.isLoaded = false;
    this.placesLibrary = null;
    this.apiLoadPromise = null;
    this.apiLoadedSubject.next(false);
    this.apiLoadingSubject.next(false);
    
    // Clean up script
    if (this.scriptElement) {
      this.scriptElement.remove();
      this.scriptElement = null;
    }
    
    console.log('✅ [GoogleMapService] Service reset completed');
  }

  // Health check method with detailed logging
  getHealthStatus(): {
    isLoaded: boolean;
    hasPlacesLibrary: boolean;
    hasSettings: boolean;
    hasApiKey: boolean;
    hasScript: boolean;
    windowGoogleAvailable: boolean;
  } {
    const status = {
      isLoaded: this.isLoaded,
      hasPlacesLibrary: !!this.placesLibrary,
      hasSettings: !!this.frontendSettings,
      hasApiKey: !!this.frontendSettings?.customSettings?.dataSource?.googleKey,
      hasScript: !!this.scriptElement && document.head.contains(this.scriptElement),
      windowGoogleAvailable: this.validateApiAvailability(),
    };
    
    console.log('🏥 [GoogleMapService] Health Status:', status);
    return status;
  }

  // Debug method to force reload
  forceReload(): Promise<boolean> {
    console.log('🔄 [GoogleMapService] Force reloading API');
    this.reset();
    return firstValueFrom(this.loadGoogleMapApi());
  }
}

export enum TravelMode {
  DRIVING = 'DRIVING',
  BICYCLING = 'BICYCLING',
  WALKING = 'WALKING',
  TRANSIT = 'TRANSIT',
}